import dayjs from "dayjs";
import i18n, {t} from "i18next";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {getContext} from "./ContextUtils";
import {LanguageUtils} from "./LanguageUtils";
import {TimeLineData} from "../components/TimeLinePicker/TimeLineData";

dayjs.extend(customParseFormat);

export const DateUtils = {

    inBetweenDates(startDate: Date, endDate: Date, dateToCheck: Date): boolean {

        if (!startDate || !endDate || !dateToCheck) {
            return false;
        }

        const start = new Date(startDate);
        const end = new Date(endDate);
        const date = new Date(dateToCheck);

        return date >= start && date <= end;
    },

    getFormat() {
        switch (getContext()) {
            case 'backofficenl':
            case 'vbk':
                return 'DD - MM - YYYY'
            default:
                return 'DD / MM / YYYY'; // Default format if context is not recognized
        }
    },

    formatDate(date: Date | null | undefined): string {
        if (!date || isNaN(date.getTime())) {
            return '-';
        }

        // French format for Belgium (even in Dutch) e.g. 31/12/2023
        const bcp47Language = LanguageUtils.changeToBCP47(i18n.language);
        if (bcp47Language === 'nl-BE') {
            return date.toLocaleDateString('fr-BE');
        }

        return date.toLocaleDateString(bcp47Language);
    },

    formatDateTime(date: Date | null | undefined): string {
        if (!date || isNaN(date.getTime()) || date.getTime() === 0) {
            return '-';
        }

        // French format for Belgium (even in Dutch) e.g. 31/12/2023 23:59
        const bcp47Language = LanguageUtils.changeToBCP47(i18n.language);
        if (bcp47Language === 'nl-BE') {
            return date.toLocaleString('fr-BE');
        }

        return date.toLocaleString(bcp47Language);
    },

    formatDateToYYYYMMDD(date: Date | null  | undefined): string {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}${month}${day}`;
    },

    formatDateToYYYYMM(date: Date | null | undefined): string {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${year}${month}`;
    },

    /**
     * Converts a date filter value (Date, ISO string, or null) to YYYY-MM-DD for range comparison.
     * Used by BMTable date-range filters and client-side filtering (e.g. CreditNoteList, InvoiceList).
     */
    toFilterDateString(value: Date | string | null | undefined): string | null {
        if (value == null) return null;
        if (value instanceof Date) {
            const y = value.getFullYear();
            const m = String(value.getMonth() + 1).padStart(2, '0');
            const d = String(value.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        }
        return value.slice(0, 10) || null;
    },

    getMonthName(monthIndex: number): string {
        const months = [
            t("January"), t("February"), t("March"), t("April"), t("May"), t("June"),
            t("July"), t("August"), t("September"), t("October"), t("November"), t("December")
        ];
        return months[monthIndex];
    },

    getPreviousMonthName(monthsBack: number): string {
        const now = new Date();
        const targetMonth = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
        return this.getMonthName(targetMonth.getMonth());
    },

    getMonthsAgo(months: number): Date {
        const d = new Date();
        d.setMonth(d.getMonth() - months);
        return d;
    },

    // Compact "YYYYMM" strings for the last `months` calendar months, oldest first.
    getLastNMonthsYYYYMM(months: number): string[] {
        const out: string[] = [];
        const now = new Date();
        for (let i = months - 1; i >= 0; i--) {
            out.push(this.formatDateToYYYYMM(new Date(now.getFullYear(), now.getMonth() - i, 1)));
        }
        return out;
    },

    formatYYYYMMToMMYYYY(dateString: string): string {
        if (!dateString || dateString.length !== 6) {
            return dateString;
        }

        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        return `${month}/${year}`;
    },

    // Localized short month + 2-digit year, e.g. "JUN '26".
    formatYYYYMMToShortMonth(yyyymm: string): string {
        if (!yyyymm || yyyymm.length !== 6) return yyyymm;
        const year = yyyymm.substring(2, 4);
        const month = parseInt(yyyymm.substring(4, 6), 10);
        return `${this.getMonthAbbreviation(month, i18n.language, true)} '${year}`;
    },

    formatYYYYMMDDToShortDay(dateString: string): string {
        if (!dateString || dateString.length !== 8) return dateString;
        return `${dateString.substring(6, 8)}/${dateString.substring(4, 6)}`;
    },

    formatYYYYMMDDToDDMMYYYY(dateString: string): string {
        if (!dateString || dateString.length !== 8) {
            return dateString;
        }

        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);
        return `${day}/${month}/${year}`;
    },

    /**
     * Parses date input: with separators (DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY),
     * or compact digits (08092025, 8092025, 892025).
     * Used by Mantine DateInput / filter date-range.
     */
    getDateParser(): (input: string) => Date | null {
        // JS Date normalises overflow silently (30/02 → 02/03, 31/04 → 01/05),
        // so we must verify the constructed Date matches the requested components.
        const buildDateIfValid = (y: number, m: number, d: number): Date | null => {
            const date = new Date(y, m - 1, d);
            if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
                return null;
            }
            return date;
        };

        const tryParseDigits = (dd: string, mm: string, yyyy: string): Date | null => {
            const d = parseInt(dd, 10);
            const m = parseInt(mm, 10);
            const y = parseInt(yyyy, 10);
            if (d >= 1 && d <= 31 && m >= 1 && m <= 12 && y >= 1900) {
                return buildDateIfValid(y, m, d);
            }
            return null;
        };

        const tryParseWithFormat = (trimmed: string, format: string): Date | null => {
            const parsed = dayjs(trimmed, format, true);
            if (!parsed.isValid()) return null;
            // dayjs strict mode has historically accepted overflow dates (e.g. 30/02),
            // so re-verify via buildDateIfValid.
            return buildDateIfValid(parsed.year(), parsed.month() + 1, parsed.date());
        };

        return (input: string) => {
            const trimmed = input?.trim() ?? '';
            if (!trimmed) return null;
            // Compact digits (no separators): 8, 7 or 6 digits
            const digits = trimmed.replace(/\D/g, '');
            if (digits.length === 8) {
                const d = tryParseDigits(digits.slice(0, 2), digits.slice(2, 4), digits.slice(4));
                if (d) return d;
            }
            if (digits.length === 7) {
                const d = tryParseDigits(digits.slice(0, 1), digits.slice(1, 3), digits.slice(3))
                    ?? tryParseDigits(digits.slice(0, 2), digits.slice(2, 3), digits.slice(3));
                if (d) return d;
            }
            if (digits.length === 6) {
                const d = tryParseDigits(digits.slice(0, 1), digits.slice(1, 2), digits.slice(2));
                if (d) return d;
            }
            // With separators: DD/MM/YYYY
            const slashed = tryParseWithFormat(trimmed, 'DD/MM/YYYY');
            if (slashed) return slashed;
            // DD - MM - YYYY
            const dashed = tryParseWithFormat(trimmed, 'DD - MM - YYYY');
            if (dashed) return dashed;
            // No lenient fallback: it silently accepts 2-digit-year inputs
            // (e.g. "25/02/26") as nonsensical dates like year 0026 or 2001.
            return null;
        };
    },

    getYearMonthFormattedDate(data: { count: number; yearMonth: string }[]): TimeLineData[] {
        if (!data || data.length === 0) {
            return [];
        }

        const timeValues: TimeLineData[] = [];
        let currentYear = "";
        let yearCount = 0;
        let months: { month: string; monthCount: number }[] = [];

        data.forEach((item, index) => {
            const year = item.yearMonth.substring(0, 4);
            const month = item.yearMonth.substring(4, 6);

            if (year !== currentYear) {
                if (currentYear !== "") {
                    timeValues.push({
                        year: currentYear,
                        yearCount: yearCount,
                        months: months
                    });
                }

                currentYear = year;
                yearCount = item.count;
                months = [{month: month, monthCount: item.count}];
            } else {
                months.push({month: month, monthCount: item.count});
                yearCount += item.count;
            }

            if (index === data.length - 1) {
                timeValues.push({
                    year: currentYear,
                    yearCount: yearCount,
                    months: months
                });
            }
        });

        return timeValues.reverse();
    },

    getMonthAbbreviation(monthNumber: number, lang: string, isShort: boolean): string {
        // Day 1 forced: setMonth() on today's date keeps the current day-of-month and JS
        // normalises the overflow (31 June → 1 July), naming the next month instead.
        const date = new Date(new Date().getFullYear(), monthNumber - 1, 1);

        let abbriviatedMonth: string;

        if (isShort) {
            abbriviatedMonth = date.toLocaleString(lang.replace("_", "-"), {month: 'short'}).toUpperCase();
        } else {
            abbriviatedMonth = date.toLocaleString(lang.replace("_", "-"), {month: 'long'});
        }

        return abbriviatedMonth;
    },

    /**
     * Calculate the number of days between today and a target date.
     * Returns positive for future dates, negative for past dates.
     * @param targetDate - The date to calculate days until/since
     * @returns Number of days (positive = future, negative = past), or 0 if no date
     */
    getDaysUntil(targetDate: Date | null | undefined): number {
        if (!targetDate) return 0;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const target = new Date(targetDate);
        target.setHours(0, 0, 0, 0);
        const diffTime = target.getTime() - today.getTime();
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
}